import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import {
  adminListCreditOrders,
  adminMarkCreditOrderPaid,
  adminProvisionCreditOrder,
  adminGetOrderReconciliation,
  adminRetryPayfastItn,
} from "@/lib/api/credits.functions";
import { adminResendPayfastReceipt } from "@/lib/api/payments-admin.functions";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Loader2, Mail, CheckCircle2, AlertTriangle, Zap, RefreshCw, History } from "lucide-react";

export const Route = createFileRoute("/_authenticated/portal/admin/credit-orders")({
  component: AdminCreditOrders,
});

type Filter = "all" | "pending" | "unprovisioned" | "paid";

function AdminCreditOrders() {
  const fetchList = useServerFn(adminListCreditOrders);
  const update = useServerFn(adminMarkCreditOrderPaid);
  const provision = useServerFn(adminProvisionCreditOrder);
  const sendInvoice = useServerFn(adminResendPayfastReceipt);
  const retryItn = useServerFn(adminRetryPayfastItn);
  const getRecon = useServerFn(adminGetOrderReconciliation);
  const qc = useQueryClient();
  const [busy, setBusy] = useState<string | null>(null);
  const [retrying, setRetrying] = useState<string | null>(null);
  const [reconOpen, setReconOpen] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>("all");
  const { data, isLoading } = useQuery({
    queryKey: ["admin-credit-orders"],
    queryFn: () => fetchList(),
  });

  const reconQuery = useQuery({
    queryKey: ["admin-credit-order-recon", reconOpen],
    queryFn: () => getRecon({ data: { reference: reconOpen! } }),
    enabled: !!reconOpen,
  });

  async function handleRetryItn(reference: string) {
    setRetrying(reference);
    try {
      const r: any = await retryItn({ data: { reference } });
      if (r?.ok) toast.success("ITN re-processed — credits granted if applicable.");
      else toast.error(`ITN replay returned HTTP ${r?.status}: ${r?.response ?? ""}`);
      qc.invalidateQueries({ queryKey: ["admin-credit-orders"] });
      qc.invalidateQueries({ queryKey: ["admin-credit-order-recon", reference] });
    } catch (e: any) {
      toast.error(e?.message ?? "Replay failed");
    } finally {
      setRetrying(null);
    }
  }

  const mut = useMutation({
    mutationFn: (vars: { id: string; status: "pending" | "paid" | "cancelled" }) =>
      update({ data: vars }),
    onSuccess: () => {
      toast.success("Updated — credits granted");
      qc.invalidateQueries({ queryKey: ["admin-credit-orders"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Failed"),
  });

  const provisionMut = useMutation({
    mutationFn: (vars: { id: string }) => provision({ data: vars }),
    onSuccess: (r: any) => {
      if (r?.alreadyGranted) {
        toast.info(`Credits already on the account (balance: ${r.balance}).`);
      } else {
        toast.success(`Provisioned ${r.issued} credits — new balance ${r.balance}.`);
      }
      qc.invalidateQueries({ queryKey: ["admin-credit-orders"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Provision failed"),
  });

  async function handleSendConfirmedInvoice(reference: string) {
    setBusy(reference);
    try {
      const r: any = await sendInvoice({ data: { reference } });
      if (r?.ok) toast.success(`Confirmed invoice emailed to ${r.email}`);
      else if (r?.queued) toast.info("Email queued for retry — we'll keep trying.");
      else toast.error(r?.reason || "Could not send invoice");
      qc.invalidateQueries({ queryKey: ["admin-credit-orders"] });
    } catch (e: any) {
      toast.error(e?.message ?? "Failed");
    } finally {
      setBusy(null);
    }
  }

  const orders = (data?.orders ?? []) as any[];
  const counts = useMemo(() => {
    const c = { all: orders.length, pending: 0, unprovisioned: 0, paid: 0 };
    for (const o of orders) {
      if (o.status === "pending") c.pending++;
      if (o.status === "paid") {
        c.paid++;
        if (!o.granted) c.unprovisioned++;
      }
    }
    return c;
  }, [orders]);
  const visible = useMemo(() => {
    switch (filter) {
      case "pending":
        return orders.filter((o) => o.status === "pending");
      case "unprovisioned":
        return orders.filter((o) => o.status === "paid" && !o.granted);
      case "paid":
        return orders.filter((o) => o.status === "paid");
      default:
        return orders;
    }
  }, [orders, filter]);

  if (isLoading) return <p className="text-sm text-muted-foreground">Loading…</p>;

  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-display text-xl text-navy">Credit pack orders</h2>
          <p className="text-xs text-muted-foreground">
            PayFast normally confirms via webhook and credits land automatically. If a
            customer paid but credits never appeared, use <strong>Provision credits</strong>
            — it's idempotent (safe to click any time, no double-credit risk).
          </p>
        </div>
        {counts.unprovisioned > 0 && (
          <div className="inline-flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
            <AlertTriangle className="h-4 w-4" />
            <span>
              <strong>{counts.unprovisioned}</strong> paid order
              {counts.unprovisioned === 1 ? "" : "s"} not yet provisioned.
            </span>
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-1.5 text-xs">
        {([
          ["all", `All (${counts.all})`],
          ["pending", `Pending (${counts.pending})`],
          ["unprovisioned", `Paid but not provisioned (${counts.unprovisioned})`],
          ["paid", `Paid (${counts.paid})`],
        ] as [Filter, string][]).map(([key, label]) => (
          <button
            key={key}
            onClick={() => setFilter(key)}
            className={`rounded-full border px-3 py-1 transition ${
              filter === key
                ? "border-navy bg-navy text-navy-foreground"
                : "border-border bg-card hover:border-navy"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {visible.length === 0 ? (
        <p className="text-sm text-muted-foreground">No orders match this filter.</p>
      ) : (
        <>
          {/* Mobile / tablet: stacked cards */}
          <ul className="space-y-3 lg:hidden">
            {visible.map((o: any) => {
              const isPaid = o.status === "paid";
              const sent = !!o.receipt_sent_at;
              const isBusy = busy === o.reference;
              const provisioning =
                provisionMut.isPending &&
                (provisionMut.variables as any)?.id === o.id;
              const needsProvision = isPaid && !o.granted;
              return (
                <li
                  key={o.id}
                  className={`rounded-xl border p-4 ${
                    needsProvision
                      ? "border-amber-300 bg-amber-50/60"
                      : "border-border bg-card"
                  }`}
                >
                  <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-3">
                    <div className="min-w-0">
                      <div className="font-mono text-[11px] break-all text-navy">
                        {o.reference}
                      </div>
                      <div className="mt-0.5 text-[11px] text-muted-foreground">
                        {new Date(o.created_at).toLocaleString("en-ZA")}
                      </div>
                    </div>
                    <span
                      className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                        isPaid
                          ? "bg-emerald-100 text-emerald-800"
                          : o.status === "cancelled"
                            ? "bg-rose-100 text-rose-800"
                            : "bg-amber-100 text-amber-800"
                      }`}
                    >
                      {o.status}
                    </span>
                  </div>

                  <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div className="min-w-0 text-xs">
                      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                        Customer
                      </div>
                      <div className="truncate font-medium text-navy">
                        {o.user?.full_name ?? "—"}
                      </div>
                      <div className="truncate text-muted-foreground">
                        {o.user?.email ?? o.user_id}
                      </div>
                    </div>
                    <div className="min-w-0 text-xs">
                      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                        Pack
                      </div>
                      <div className="font-medium">{o.pack_code}</div>
                      <div className="text-muted-foreground">
                        {o.credits} credits · R{Number(o.amount_zar).toFixed(2)}
                      </div>
                    </div>
                  </div>

                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {o.granted ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] text-emerald-700">
                        <CheckCircle2 className="h-3 w-3" /> Credits issued
                      </span>
                    ) : isPaid ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800">
                        <AlertTriangle className="h-3 w-3" /> Not provisioned
                      </span>
                    ) : null}
                    {sent ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] text-emerald-700">
                        <Mail className="h-3 w-3" /> Invoice sent
                      </span>
                    ) : isPaid ? (
                      <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">
                        Invoice not sent
                      </span>
                    ) : null}
                  </div>

                  <div className="mt-3 flex flex-wrap gap-1.5 border-t border-border/60 pt-3">
                    <OrderActions
                      o={o}
                      isPaid={isPaid}
                      sent={sent}
                      isBusy={isBusy}
                      provisioning={provisioning}
                      needsProvision={needsProvision}
                      retrying={retrying === o.reference}
                      onMarkPaid={() => mut.mutate({ id: o.id, status: "paid" })}
                      onProvision={() => provisionMut.mutate({ id: o.id })}
                      onCancel={() => mut.mutate({ id: o.id, status: "cancelled" })}
                      onSendInvoice={() => handleSendConfirmedInvoice(o.reference)}
                      onHistory={() => setReconOpen(o.reference)}
                      onRetryItn={() => handleRetryItn(o.reference)}
                    />
                  </div>
                </li>
              );
            })}
          </ul>

          {/* Desktop: full table */}
          <div className="hidden overflow-x-auto rounded-xl border border-border bg-card lg:block">
            <table className="w-full text-sm">
              <thead className="bg-muted text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 w-[24%]">Order</th>
                  <th className="px-3 py-2 w-[20%]">User</th>
                  <th className="px-3 py-2 w-[14%]">Pack</th>
                  <th className="px-3 py-2 w-[16%]">Status</th>
                  <th className="px-3 py-2 w-[26%] text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {visible.map((o: any) => {
                  const isPaid = o.status === "paid";
                  const sent = !!o.receipt_sent_at;
                  const isBusy = busy === o.reference;
                  const provisioning =
                    provisionMut.isPending &&
                    (provisionMut.variables as any)?.id === o.id;
                  const needsProvision = isPaid && !o.granted;
                  return (
                    <tr
                      key={o.id}
                      className={needsProvision ? "bg-amber-50/50 align-top" : "align-top"}
                    >
                      <td className="px-3 py-3 min-w-0">
                        <div className="font-mono text-[11px] break-all text-navy">
                          {o.reference}
                        </div>
                        <div className="mt-0.5 text-[11px] text-muted-foreground">
                          {new Date(o.created_at).toLocaleString("en-ZA")}
                        </div>
                      </td>
                      <td className="px-3 py-3 min-w-0 text-xs">
                        <div className="truncate font-medium text-navy">
                          {o.user?.full_name ?? "—"}
                        </div>
                        <div className="break-all text-muted-foreground">
                          {o.user?.email ?? o.user_id}
                        </div>
                      </td>
                      <td className="px-3 py-3 text-xs">
                        <div className="font-medium">{o.pack_code}</div>
                        <div className="text-muted-foreground">
                          {o.credits} credits · R{Number(o.amount_zar).toFixed(2)}
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex flex-col items-start gap-1">
                          <span
                            className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                              isPaid
                                ? "bg-emerald-100 text-emerald-800"
                                : o.status === "cancelled"
                                  ? "bg-rose-100 text-rose-800"
                                  : "bg-amber-100 text-amber-800"
                            }`}
                          >
                            {o.status}
                          </span>
                          {o.granted ? (
                            <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700">
                              <CheckCircle2 className="h-3 w-3" /> Credits issued
                            </span>
                          ) : isPaid ? (
                            <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700">
                              <AlertTriangle className="h-3 w-3" /> Not provisioned
                            </span>
                          ) : null}
                          {sent ? (
                            <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700">
                              <Mail className="h-3 w-3" /> Invoice sent
                            </span>
                          ) : isPaid ? (
                            <span className="text-[11px] text-muted-foreground">
                              Invoice not sent
                            </span>
                          ) : null}
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex flex-wrap justify-end gap-1.5">
                          <OrderActions
                            o={o}
                            isPaid={isPaid}
                            sent={sent}
                            isBusy={isBusy}
                            provisioning={provisioning}
                            needsProvision={needsProvision}
                            retrying={retrying === o.reference}
                            onMarkPaid={() => mut.mutate({ id: o.id, status: "paid" })}
                            onProvision={() => provisionMut.mutate({ id: o.id })}
                            onCancel={() => mut.mutate({ id: o.id, status: "cancelled" })}
                            onSendInvoice={() => handleSendConfirmedInvoice(o.reference)}
                            onHistory={() => setReconOpen(o.reference)}
                            onRetryItn={() => handleRetryItn(o.reference)}
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}


      <Dialog open={!!reconOpen} onOpenChange={(v) => !v && setReconOpen(null)}>
        <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Reconciliation history</DialogTitle>
            <DialogDescription className="font-mono text-xs">
              {reconOpen}
            </DialogDescription>
          </DialogHeader>
          {reconQuery.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : reconQuery.data ? (
            <ReconciliationDetails data={reconQuery.data} />
          ) : (
            <p className="text-sm text-muted-foreground">No data.</p>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}

function OrderActions(props: {
  o: any;
  isPaid: boolean;
  sent: boolean;
  isBusy: boolean;
  provisioning: boolean;
  needsProvision: boolean;
  retrying: boolean;
  onMarkPaid: () => void;
  onProvision: () => void;
  onCancel: () => void;
  onSendInvoice: () => void;
  onHistory: () => void;
  onRetryItn: () => void;
}) {
  const {
    o, isPaid, sent, isBusy, provisioning, needsProvision, retrying,
    onMarkPaid, onProvision, onCancel, onSendInvoice, onHistory, onRetryItn,
  } = props;
  return (
    <>
      {!isPaid && (
        <Button
          size="sm"
          onClick={onMarkPaid}
          className="h-7 bg-emerald-600 px-2 text-[11px] hover:bg-emerald-700"
        >
          Mark paid
        </Button>
      )}
      {(needsProvision || !isPaid) && (
        <Button
          size="sm"
          variant={needsProvision ? "default" : "outline"}
          disabled={provisioning}
          onClick={onProvision}
          className={`h-7 px-2 text-[11px] ${
            needsProvision ? "bg-amber-600 text-white hover:bg-amber-700" : ""
          }`}
          title="Idempotent — safe to click any time; never double-credits."
        >
          {provisioning ? (
            <Loader2 className="mr-1 h-3 w-3 animate-spin" />
          ) : (
            <Zap className="mr-1 h-3 w-3" />
          )}
          Provision
        </Button>
      )}
      {o.status === "pending" && (
        <Button size="sm" variant="outline" onClick={onCancel} className="h-7 px-2 text-[11px]">
          Cancel
        </Button>
      )}
      {isPaid && (
        <Button
          size="sm"
          variant={sent ? "outline" : "default"}
          disabled={isBusy}
          onClick={onSendInvoice}
          className="h-7 px-2 text-[11px]"
        >
          {isBusy ? (
            <Loader2 className="mr-1 h-3 w-3 animate-spin" />
          ) : (
            <Mail className="mr-1 h-3 w-3" />
          )}
          {sent ? "Re-send" : "Send invoice"}
        </Button>
      )}
      <Button
        size="sm"
        variant="outline"
        onClick={onHistory}
        title="View reconciliation history & ITN attempts"
        className="h-7 px-2 text-[11px]"
      >
        <History className="mr-1 h-3 w-3" />
        History
      </Button>
      <Button
        size="sm"
        variant="outline"
        disabled={retrying}
        onClick={onRetryItn}
        title="Re-process the most recent PayFast ITN payload for this order"
        className="h-7 px-2 text-[11px]"
      >
        {retrying ? (
          <Loader2 className="mr-1 h-3 w-3 animate-spin" />
        ) : (
          <RefreshCw className="mr-1 h-3 w-3" />
        )}
        Retry ITN
      </Button>
    </>
  );
}

function ReconciliationDetails({ data }: { data: any }) {
  const { ledgerEntries, itnAttempts, reconciliationHistory, provisioned, order } = data;
  return (
    <div className="space-y-5 text-sm">
      <div className="rounded-lg border border-border bg-muted/40 p-3">
        <div className="flex items-center justify-between">
          <span className="font-semibold">Current ledger provisioning</span>
          {provisioned ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-800">
              <CheckCircle2 className="h-3.5 w-3.5" /> Provisioned
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800">
              <AlertTriangle className="h-3.5 w-3.5" /> Not in ledger
            </span>
          )}
        </div>
        {ledgerEntries.length > 0 ? (
          <ul className="mt-2 space-y-1 text-xs">
            {ledgerEntries.map((l: any) => (
              <li key={l.id} className="flex items-center justify-between">
                <span>+{l.delta} credits · {l.reason}</span>
                <span className="text-muted-foreground">
                  {new Date(l.created_at).toLocaleString("en-ZA")}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-2 text-xs text-muted-foreground">
            No <code>credit_ledger</code> row references this order yet. Use
            <strong> Provision credits</strong> or <strong>Retry ITN</strong>.
          </p>
        )}
      </div>

      <div>
        <h4 className="mb-2 font-semibold">Reconciliation runs ({reconciliationHistory.length})</h4>
        {reconciliationHistory.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            No automated reconciliation run has touched this order yet.
          </p>
        ) : (
          <ul className="space-y-1.5">
            {reconciliationHistory.map((h: any) => (
              <li
                key={h.run_id}
                className="rounded-md border border-border bg-card px-3 py-2 text-xs"
              >
                <div className="flex items-center justify-between">
                  <span className="font-semibold">
                    {h.entry?.status === "provisioned"
                      ? "✅ Provisioned"
                      : h.entry?.status === "error"
                        ? "❌ Error"
                        : h.entry?.status ?? "—"}
                  </span>
                  <span className="text-muted-foreground">
                    {new Date(h.started_at).toLocaleString("en-ZA")} · {h.triggered_by}
                  </span>
                </div>
                {h.entry?.error && (
                  <p className="mt-1 text-rose-700">{h.entry.error}</p>
                )}
                {typeof h.entry?.new_balance === "number" && (
                  <p className="mt-0.5 text-muted-foreground">
                    New balance: {h.entry.new_balance}
                  </p>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div>
        <h4 className="mb-2 font-semibold">PayFast ITN attempts ({itnAttempts.length})</h4>
        {itnAttempts.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            No ITN webhook has been received for this reference. Retry isn't possible
            until PayFast delivers at least one webhook.
          </p>
        ) : (
          <ul className="space-y-1.5">
            {itnAttempts.map((a: any) => (
              <li
                key={a.id}
                className="rounded-md border border-border bg-card px-3 py-2 text-xs"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-mono">{a.payment_status ?? "—"}</span>
                  <span className="text-muted-foreground">
                    {new Date(a.created_at).toLocaleString("en-ZA")} · attempt #{a.attempt_no}
                  </span>
                </div>
                <div className="mt-1 grid grid-cols-2 gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground">
                  <span>sig: {String(a.signature_valid)}</span>
                  <span>outcome: {a.webhook_outcome ?? a.grant_status}</span>
                  <span>validate: {a.server_validate_result}</span>
                  <span>pf_id: {a.pf_payment_id ?? "—"}</span>
                </div>
                {a.grant_error && (
                  <p className="mt-1 text-rose-700">{a.grant_error}</p>
                )}
                {a.notes && <p className="mt-0.5 text-muted-foreground">{a.notes}</p>}
              </li>
            ))}
          </ul>
        )}
      </div>

      {order && (
        <p className="text-[11px] text-muted-foreground">
          Order created {new Date(order.created_at).toLocaleString("en-ZA")} ·
          {order.paid_at ? ` paid ${new Date(order.paid_at).toLocaleString("en-ZA")}` : " not paid"}
        </p>
      )}
    </div>
  );
}

